import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AssignmentPicker from './AssignmentPicker';
import { aufgabeApi, type Aufgabe } from '../api/aufgabe';
import type { AufgabenEntityType } from '../api/aufgaben';
import { downloadIcs } from '../utils/ics';

interface AufgabenBlockProps {
  entityType: AufgabenEntityType;
  entityId: number;
  /** Zusätzliche Query-Keys, die nach Änderungen aktualisiert werden (z. B. die Detailseite). */
  invalidateKeys?: (string | number)[][];
}

function isOverdue(d: string): boolean {
  return new Date(d) < new Date(new Date().toDateString());
}

function overdueLabel(d: string): string {
  const startOfToday = new Date(new Date().toDateString()).getTime();
  const dueDay = new Date(new Date(d).toDateString()).getTime();
  const days = Math.floor((startOfToday - dueDay) / 86400000);
  if (days <= 0) return '';
  return ` (überfällig seit ${days} ${days === 1 ? 'Tag' : 'Tagen'})`;
}

export default function AufgabenBlock({ entityType, entityId, invalidateKeys = [] }: AufgabenBlockProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [bearbeiterId, setBearbeiterId] = useState<number | null>(null);
  const [faelligkeit, setFaelligkeit] = useState('');
  const [alsoZustaendig, setAlsoZustaendig] = useState(false);

  const queryKey = ['aufgaben', 'for-entity', entityType, entityId];
  const { data: aufgaben = [] } = useQuery({
    queryKey,
    queryFn: () => aufgabeApi.forEntity(entityType, entityId),
  });

  const { data: suggestionData } = useQuery({
    queryKey: ['aufgaben', 'suggestions', entityType, entityId],
    queryFn: () => aufgabeApi.suggestions(entityType, entityId),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['aufgaben'] });
    for (const key of invalidateKeys) queryClient.invalidateQueries({ queryKey: key });
  };

  const suggestMutation = useMutation({
    mutationFn: (s: { title: string; faelligkeit: string | null }) =>
      aufgabeApi.create({ entity_type: entityType, entity_id: entityId, title: s.title, faelligkeit: s.faelligkeit }),
    onSuccess: invalidateAll,
  });

  const openSuggestions = (suggestionData?.suggestions ?? []).filter((s) => !s.already_open);

  const createMutation = useMutation({
    mutationFn: () =>
      aufgabeApi.create({
        entity_type: entityType,
        entity_id: entityId,
        title: title.trim(),
        bearbeiter_user_id: bearbeiterId ?? undefined,
        faelligkeit: faelligkeit ? `${faelligkeit}T00:00:00` : null,
        also_set_zustaendig: alsoZustaendig,
      }),
    onSuccess: () => {
      invalidateAll();
      setShowForm(false);
      setTitle('');
      setBearbeiterId(null);
      setFaelligkeit('');
      setAlsoZustaendig(false);
    },
  });

  const completeMutation = useMutation({
    mutationFn: (a: Aufgabe) => aufgabeApi.complete(a.id),
    onSuccess: invalidateAll,
  });
  const reopenMutation = useMutation({
    mutationFn: (a: Aufgabe) => aufgabeApi.reopen(a.id),
    onSuccess: invalidateAll,
  });
  const deleteMutation = useMutation({
    mutationFn: (a: Aufgabe) => aufgabeApi.remove(a.id),
    onSuccess: invalidateAll,
  });

  const offen = aufgaben.filter((a) => a.status === 'offen');
  const erledigt = aufgaben.filter((a) => a.status === 'erledigt');

  const exportIcs = (a: Aufgabe) => {
    if (!a.faelligkeit) return;
    downloadIcs(`aufgabe-${a.id}.ics`, [{
      uid: `aufgabe-${a.id}@fewo-monitor`,
      summary: a.title,
      date: a.faelligkeit.slice(0, 10),
      description: a.description ?? undefined,
      url: window.location.href,
    }]);
  };

  const renderRow = (a: Aufgabe) => {
    const overdue = a.status === 'offen' && a.faelligkeit && isOverdue(a.faelligkeit);
    return (
      <div key={a.id} className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <p className={`text-sm ${a.status === 'erledigt' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
            {a.title}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {a.bearbeiter ? `Bearbeiter: ${a.bearbeiter.full_name}` : 'Kein Bearbeiter'}
            {a.faelligkeit && (
              <span className={overdue ? 'text-red-600 font-medium' : ''}>
                {' · fällig '}{new Date(a.faelligkeit).toLocaleDateString('de-DE')}{overdue ? overdueLabel(a.faelligkeit) : ''}
              </span>
            )}
            {a.status === 'erledigt' && a.erledigt_at && (
              <span> · erledigt {new Date(a.erledigt_at).toLocaleDateString('de-DE')}</span>
            )}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2 text-xs">
          {a.faelligkeit && (
            <button onClick={() => exportIcs(a)} className="text-blue-600 hover:text-blue-800" title="In Kalender exportieren">
              In Kalender
            </button>
          )}
          {a.status === 'offen' ? (
            <button onClick={() => completeMutation.mutate(a)} className="text-green-700 hover:text-green-900">Erledigt</button>
          ) : (
            <button onClick={() => reopenMutation.mutate(a)} className="text-gray-600 hover:text-gray-800">Wieder öffnen</button>
          )}
          <button onClick={() => deleteMutation.mutate(a)} className="text-gray-400 hover:text-red-600" title="Löschen">✕</button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-medium">
          Aufgaben
          {offen.length > 0 && <span className="ml-2 text-sm font-normal text-gray-500">({offen.length} offen)</span>}
        </h3>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="text-sm text-blue-600 hover:text-blue-800">
            + Neue Aufgabe
          </button>
        )}
      </div>

      {showForm && (
        <div className="px-6 py-4 border-b bg-gray-50 space-y-3">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Was ist zu tun? (z. B. Prüfen ob Inserat offline)"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bearbeiter</label>
              <AssignmentPicker value={bearbeiterId} onChange={(uid) => setBearbeiterId(uid)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fällig (optional)</label>
              <input type="date" value={faelligkeit} onChange={(e) => setFaelligkeit(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={alsoZustaendig} onChange={(e) => setAlsoZustaendig(e.target.checked)} />
            Zuständigkeit auf den Bearbeiter übergeben
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => createMutation.mutate()}
              disabled={!title.trim() || createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Wird angelegt…' : 'Aufgabe anlegen'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {openSuggestions.length > 0 && (
        <div className="px-6 py-3 border-b bg-blue-50/60">
          <p className="text-xs font-medium text-gray-600 mb-2">
            Vorgeschlagene nächste Aufgaben{suggestionData?.status_label ? ` (Status: ${suggestionData.status_label})` : ''}
          </p>
          <div className="flex flex-wrap gap-2">
            {openSuggestions.map((s) => (
              <button
                key={s.title}
                onClick={() => suggestMutation.mutate({ title: s.title, faelligkeit: s.faelligkeit })}
                disabled={suggestMutation.isPending}
                className="text-xs bg-white border border-blue-200 text-blue-700 rounded-full px-3 py-1 hover:bg-blue-100 disabled:opacity-50"
                title="Aufgabe aus Vorlage anlegen"
              >
                + {s.title}{s.frist_tage != null ? ` (fällig in ${s.frist_tage} Tagen)` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {offen.length === 0 && erledigt.length === 0 ? (
        <p className="px-6 py-6 text-sm text-gray-500 text-center">Keine Aufgaben.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {offen.map(renderRow)}
          {erledigt.length > 0 && (
            <details className="group">
              <summary className="px-4 py-2 text-xs text-gray-500 cursor-pointer hover:bg-gray-50">
                {erledigt.length} erledigte Aufgabe{erledigt.length !== 1 ? 'n' : ''} anzeigen
              </summary>
              <div className="divide-y divide-gray-100">{erledigt.map(renderRow)}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
